import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function MakeFriendsVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "make-friends-fast-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "students": "Making Friends as a Student",
    "professionals": "Young Professionals",
    "newcomers-expats": "Newcomers & Expats",
    "general-strategies": "General Strategies",
    "online-platforms": "Online Platforms",
    "social-events": "Social Events & Activities",
    "hobbies-meetups": "Hobbies & Meetups",
    "volunteering": "Volunteering",
    "fitness-wellness": "Fitness & Wellness",
    "key-locations": "Key Locations",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References"
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/people-programs/volunteering.aspx",
      label: "City of Vancouver – Volunteering"
    },
    {
      url: "https://vancouvernewcomersclub.com/",
      label: "Vancouver Newcomers Club"
    },
    {
      url: "https://www.meetup.com/cities/ca/bc/vancouver/",
      label: "Meetup – Vancouver Social Groups"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have 8 key locations; each matches the content in the “key-locations” section.
  const locations = [
    {
      id: "loc-vpl",
      lat: 49.27979,
      lng: -123.11567,
      popupText: "Vancouver Public Library – Central Library (Downtown)"
    },
    {
      id: "loc-roundhouse",
      lat: 49.27455,
      lng: -123.12190,
      popupText: "Roundhouse Community Arts & Recreation Centre"
    },
    {
      id: "loc-robson-square",
      lat: 49.28194,
      lng: -123.12194,
      popupText: "Robson Square & Art Gallery Plaza"
    },
    {
      id: "loc-english-bay",
      lat: 49.28627,
      lng: -123.14359,
      popupText: "English Bay Beach (West End)"
    },
    {
      id: "loc-trout-lake",
      lat: 49.25750,
      lng: -123.06287,
      popupText: "Trout Lake & Trout Lake Community Centre"
    },
    {
      id: "loc-jericho",
      lat: 49.27558,
      lng: -123.20143,
      popupText: "Jericho Beach & Jericho Sailing Centre"
    },
    {
      id: "loc-west-end-cc",
      lat: 49.28962,
      lng: -123.13682,
      popupText: "West End Community Centre & Ice Rink"
    },
    {
      id: "loc-science-world",
      lat: 49.27325,
      lng: -123.10377,
      popupText: "Science World (TELUS World of Science)"
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>How to Make Friends Quickly in Vancouver: A Practical Guide</title>
        <meta
          name="description"
          content="A comprehensive guide on making friends fast in Vancouver, including local strategies, key locations, online platforms, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver, make friends, newcomers, professionals, students, social events, volunteering, networking"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/make-friends-fast-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="How to Make Friends Quickly in Vancouver: A Practical Guide"
        />
        <meta
          property="og:description"
          content="A detailed, actionable guide for students, professionals, and newcomers to make friends in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/make-friends-fast-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/make-friends-fast-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="How to Make Friends Quickly in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover tips, local hotspots, and practical steps to meet new people in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/make-friends-fast-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A group of friends at English Bay in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is world-renowned for its stunning scenery and vibrant
              multicultural atmosphere. Yet for many newcomers and even locals,
              making friends can feel challenging. Some refer to this as the
              “Vancouver freeze,” suggesting a reserved social climate that
              keeps social circles closed. This guide offers practical, real-life
              tips to help you break the ice and build meaningful friendships—
              whether you're a student, a professional, a newcomer, or anyone
              simply looking to expand your social circle in Vancouver.
            </p>

            {/* SECTION: Students */}
            <h2 id="students">Making Friends as a Student</h2>
            <p>
              If you're a student at UBC, SFU, BCIT, or any other institution,
              you already have access to campus clubs, orientation events,
              and study groups. Joining clubs that align with your interests—
              from academic societies to cultural or sports clubs—instantly
              connects you to like-minded peers. Take advantage of campus
              events such as pub nights, open mic evenings, and orientation
              activities specifically designed to help students meet. Also,
              consider study groups or casual meetups with classmates. A simple
              “Want to grab coffee after class?” can be the perfect way
              to foster new friendships.
            </p>

            {/* SECTION: Young Professionals */}
            <h2 id="professionals">Young Professionals</h2>
            <p>
              For working professionals, networking events and after-work
              activities are key. Industry-focused gatherings hosted by local
              business associations or interest-based groups on sites like
              Meetup.com offer a relaxed setting to expand your professional
              and personal network. Consider joining local sports leagues
              (e.g., Urban Rec) where teams socialize post-game. Volunteering
              at conferences or workshops in your field can also open doors to
              new connections beyond the office.
            </p>

            {/* SECTION: Newcomers & Expats */}
            <h2 id="newcomers-expats">Newcomers & Expats</h2>
            <p>
              If you’re new to Vancouver, you’re in good company. The city
              welcomes people from all over the world, and numerous programs
              (e.g., Vancouver Newcomers Club) help connect fresh arrivals.
              Immigrant service organizations often host language exchanges
              and cultural events. Take advantage of community center programs,
              newcomer meetups, and neighborhood Facebook groups. Don’t be shy
              about mentioning you’re new in town—Vancouverites are often
              happy to introduce you to their favorite spots or social circles.
            </p>

            {/* SECTION: General Strategies */}
            <h2 id="general-strategies">General Strategies</h2>
            <p>
              Regardless of your background, certain strategies apply to
              everyone looking to expand their social circle. Be open and
              approachable in daily interactions—chat with neighbors, local
              baristas, or workout classmates. Consistency helps: frequent the
              same café or fitness class so people recognize you over time.
              Accept invitations to parties, community events, or casual
              hangouts and don’t hesitate to invite acquaintances to do
              something fun.
            </p>

            {/* SECTION: Online Platforms */}
            <h2 id="online-platforms">Online Platforms</h2>
            <p>
              Using digital tools is a quick way to find potential friends who
              share your interests. Platforms like Meetup.com or Facebook
              Groups have hundreds of Vancouver-based communities for
              everything from hiking to board games. Bumble BFF is a popular
              app dedicated to making platonic friendships. Just remember to
              meet in public places and follow basic safety precautions when
              transitioning an online connection to an in-person meetup.
            </p>

            {/* SECTION: Social Events & Activities */}
            <h2 id="social-events">Social Events & Activities</h2>
            <p>
              Vancouver hosts a variety of community festivals, cultural
              celebrations, and casual social events all year. Street festivals,
              art walks, free dance lessons at Robson Square, pub trivia nights,
              and meetup-based outings are perfect for mingling in a
              laid-back atmosphere. By attending or volunteering at these
              events, you can meet a wide range of people quickly.
            </p>

            {/* SECTION: Hobbies & Meetups */}
            <h2 id="hobbies-meetups">Hobbies & Interest-Based Meetups</h2>
            <p>
              One of the best ways to form lasting friendships is to connect
              with people over shared passions. Whether it’s joining an outdoor
              adventure group for hiking, a writers’ club, or a film discussion
              group, focusing on activities you love naturally fosters common
              ground. Meetup.com, local Facebook groups, and community center
              boards are great resources to find niche hobby groups—be it board
              games, painting, coding, or cooking clubs.
            </p>

            {/* SECTION: Volunteering */}
            <h2 id="volunteering">Volunteering</h2>
            <p>
              Volunteering can be a rewarding way to meet people who share
              similar values. Vancouver offers countless opportunities: from
              local food banks and animal shelters to major film festivals and
              community centers. Working side-by-side for a cause you believe
              in can quickly transform casual acquaintances into genuine
              friendships.
            </p>

            {/* SECTION: Fitness & Wellness */}
            <h2 id="fitness-wellness">Fitness & Wellness</h2>
            <p>
              Vancouver’s active culture means there are plenty of fitness
              groups—from running and cycling clubs to co-ed sports leagues and
              yoga classes. Participating in group workouts, joining a CrossFit
              “box,” or signing up for local boot camps not only improves your
              health but also helps you make new connections. Consider
              consistent attendance and post-workout meetups (like grabbing a
              smoothie together) to strengthen budding friendships.
            </p>

            {/* SECTION: Key Locations */}
            <h2 id="key-locations">Key Locations</h2>
            <p>
              Here are several notable places where people naturally gather,
              making it easy to strike up conversations and expand your social
              network.
            </p>

            {/* Vancouver Public Library */}
            <p
              ref={(el) => setSpotRef("loc-vpl", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Public Library – Central Library (Downtown)</strong>{" "}
              <Marker
                coordY={49.27979}
                coordX={-123.11567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A community hub offering free events, author talks, and study
              spaces. Perfect for casual meetups or joining free workshops.
            </p>

            {/* Roundhouse */}
            <p
              ref={(el) => setSpotRef("loc-roundhouse", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Roundhouse Community Arts & Recreation Centre</strong>{" "}
              <Marker
                coordY={49.27455}
                coordX={-123.12190}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – This historic Yaletown facility hosts art exhibitions, dance
              classes, and community programs that attract diverse crowds.
            </p>

            {/* Robson Square */}
            <p
              ref={(el) => setSpotRef("loc-robson-square", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Robson Square & Art Gallery Plaza</strong>{" "}
              <Marker
                coordY={49.28194}
                coordX={-123.12194}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Central downtown space for ice skating in winter, free dance
              classes in summer, and social gatherings year-round.
            </p>

            {/* English Bay */}
            <p
              ref={(el) => setSpotRef("loc-english-bay", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>English Bay Beach (West End)</strong>{" "}
              <Marker
                coordY={49.28627}
                coordX={-123.14359}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A popular beach for volleyball, sunset watching, and mingling
              during the summer months.
            </p>

            {/* Trout Lake */}
            <p
              ref={(el) => setSpotRef("loc-trout-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Trout Lake & Trout Lake Community Centre</strong>{" "}
              <Marker
                coordY={49.25750}
                coordX={-123.06287}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Home to a vibrant farmers market, drop-in sports, and
              neighborhood events in East Vancouver.
            </p>

            {/* Jericho Beach */}
            <p
              ref={(el) => setSpotRef("loc-jericho", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Jericho Beach & Jericho Sailing Centre</strong>{" "}
              <Marker
                coordY={49.27558}
                coordX={-123.20143}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Ideal for picnics, water sports, and casual meetups, with
              stunning views of downtown and the mountains.
            </p>

            {/* West End CC */}
            <p
              ref={(el) => setSpotRef("loc-west-end-cc", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>West End Community Centre & Ice Rink</strong>{" "}
              <Marker
                coordY={49.28962}
                coordX={-123.13682}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Offers diverse programs, from dance classes to hockey. A social
              anchor for residents in a bustling neighborhood.
            </p>

            {/* Science World */}
            <p
              ref={(el) => setSpotRef("loc-science-world", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Science World (TELUS World of Science)</strong>{" "}
              <Marker
                coordY={49.27325}
                coordX={-123.10377}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A landmark museum offering events like “After Dark” adult-only
              socials, and a popular seawall gathering spot at Olympic Village.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              When attending large gatherings in Vancouver’s public parks,
              keep in mind that permits may be required for significant
              organized events. Public consumption of alcohol is limited
              to designated areas in some pilot programs. Also, smoking
              (including cannabis) is prohibited in parks and beaches except
              in certain zones. Always check for official guidelines or
              temporary bylaws on the City of Vancouver website.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Is it really that difficult to make friends in Vancouver?</h3>
              <p>
                The “Vancouver freeze” is partly a myth. While locals might
                appear reserved at first, many are open and welcoming once you
                take the initiative. Attend meetups, say “yes” to social
                invitations, and use the resources in this guide to break the
                ice.
              </p>

              <h3>2. Are there free or budget-friendly ways to meet new people?</h3>
              <p>
                Absolutely! Many activities—like community center drop-ins,
                free dance classes at Robson Square, or volunteering—are
                low-cost or free. Even grabbing coffee with a potential friend
                is a wallet-friendly way to start building connections.
              </p>

              <h3>3. What if I’m shy or introverted?</h3>
              <p>
                Start with smaller group settings or volunteer roles that
                provide a structured way to interact. Online communities,
                book clubs, and quieter meetup groups can help you ease into
                socializing at your own pace.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Making friends quickly in Vancouver may seem daunting at first,
              but the city’s diverse social landscape offers countless
              opportunities. By exploring community events, leveraging online
              platforms, volunteering, and frequenting friendly gathering
              spots, you can form real connections. Remember, consistency and
              willingness to say “yes” are key. Good luck, and here’s to
              your new Vancouver friends!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}